// v14at-p4 F4 — ReviewMeasurementDialog: "Bekijk meting" sheet.
//
// Hergebruikt `AutoMeasureDetailsDialog` ongewijzigd als sub-component voor
// de details-presentatie en biedt daarbovenop de apply-controls
// (ComponentPicker + keep/replace).
//
// v14at-p5 — Apply-pad gesplitst op basis van backend-respons:
//   • Nieuwe atomic pad (flag=AUTOMEASURE_APPLY_WRITES_SURFACES=on): backend
//     levert `response.facade_surfaces` + volledige `automeasure_building`.
//     FE roept dan `onApplyServerAuthored(response)` en wordt pure consument
//     (geen afhankelijkheid van 1500 ms autosave meer).
//   • Legacy pad (flag=off): backend levert enkel `automeasure_building`
//     metadata; FE roept `onMeasuredFallback(result, strategy, ids)` om de
//     surfaces lokaal te materialiseren (oude gedrag).
import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import AutoMeasureDetailsDialog, { AutoMeasureDetailsContent } from "../automeasure/AutoMeasureDetailsDialog";
import { ComponentPicker, ComponentPickerSummary } from "../automeasure/ComponentPicker";

export function ReviewMeasurementDialog({
  open,
  onOpenChange,
  job,
  hasExistingSurfaces,
  onApply, // async ({strategy, selectedComponentIds}) => applyResponse
  onApplyServerAuthored, // v14at-p5 — async (applyResponse) => void (nieuwe atomic pad)
  onMeasuredFallback, // async (result, strategy, ids) => void (legacy pad)
}) {
  const result = job?.result || null;
  const comps = useMemo(() => result?.components || [], [result]);
  const meaningful = useMemo(
    () => comps.filter((c) => (c.physical_facades || []).some((f) => !f.is_shared)),
    [comps]
  );

  const [selected, setSelected] = useState(
    () => new Set(meaningful.filter((c) => c.include_by_default).map((c) => c.component_id))
  );
  const [staleError, setStaleError] = useState(null);
  const [buildingConflict, setBuildingConflict] = useState(null);
  const [applying, setApplying] = useState(false);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const doApply = async (strategy) => {
    if (!onApply || !result) return;
    setStaleError(null);
    setBuildingConflict(null);
    setApplying(true);
    try {
      const ids = Array.from(selected);
      const response = await onApply({ strategy, selectedComponentIds: ids });

      // v14at-p5 — Als backend facade_surfaces atomisch heeft geschreven,
      // vermijd dubbele materialisatie via onMeasuredFallback. FE neemt
      // de surfaces + metadata rechtstreeks uit de response over.
      if (response && Array.isArray(response.facade_surfaces) && onApplyServerAuthored) {
        await onApplyServerAuthored(response);
      } else if (onMeasuredFallback) {
        // Legacy pad — FE genereert zelf gevelvlakken.
        await onMeasuredFallback(result, strategy, ids.length ? ids : undefined);
      }

      toast.success("Gemeten met AutoMeasure™ — gevelvlakken aangemaakt.");
      onOpenChange(false);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "stale_measurement") {
        setStaleError(e.response.data.detail);
      } else if (code === "building_identity_conflict") {
        setBuildingConflict(e.response.data.detail);
      } else if (code === "no_facades_to_apply") {
        toast.error(
          e.response?.data?.detail?.message ||
            "AutoMeasure vond geen schilderbare gevels om toe te passen."
        );
      } else {
        const msg = e?.response?.data?.detail?.message || e?.message || "Toepassen mislukt";
        toast.error(msg);
      }
    } finally {
      setApplying(false);
    }
  };

  if (!result) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* v14at-p7 — 3-zone sticky layout:
            • Header (shrink-0) blijft bovenaan.
            • Body (flex-1 overflow-y-auto) scrollt met alle review-content.
            • Footer (shrink-0 border-t) toont ALTIJD de live-samenvatting
              + Annuleren + Meting toepassen, ongeacht contenthoogte.
          Verwijdert daarmee de root cause van de P0-A/B/C/D cascade uit
          `v14at_p7_review_dialog_rca.md`. Geen z-index/pointer-events hacks;
          Radix focus/a11y + default `×` rechtsboven behouden. */}
      <DialogContent
        className="max-w-2xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden"
        data-testid="review-measurement-dialog"
      >
        <DialogHeader className="shrink-0 px-6 pt-6 pb-3 border-b border-[#E5E2DC]">
          <DialogTitle>AutoMeasure resultaat</DialogTitle>
          <DialogDescription>
            Controleer het resultaat en kies welke bouwdelen je wil toepassen.
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex-1 overflow-y-auto px-6 py-4 space-y-3"
          data-testid="review-measurement-scroll-body"
        >
          {staleError && (
            <div
              className="rounded-md border border-[#B48A33]/60 bg-[#FDF8EB] p-3 text-[12px] text-[#7A5A1A]"
              data-testid="review-measurement-stale"
            >
              <strong>Zone-adres is gewijzigd.</strong>
              <div className="mt-1">
                Deze meting betreft adres <em>{staleError.job_address}</em>, maar de
                zone verwijst nu naar <em>{staleError.zone_address}</em>. Start een
                nieuwe meting om verder te gaan.
              </div>
            </div>
          )}
          {buildingConflict && (
            <div
              className="rounded-md border border-[#B48A33]/60 bg-[#FDF8EB] p-3 text-[12px] text-[#7A5A1A]"
              data-testid="review-measurement-building-conflict"
            >
              <strong>Ander gebouw gedetecteerd.</strong>
              <div className="mt-1">
                De zone verwees eerder naar gebouw{" "}
                <code>{buildingConflict.existing_building_id}</code>; deze meting
                beschrijft gebouw <code>{buildingConflict.new_building_id}</code>.
                Kies "Vervangen" om toch toe te passen.
              </div>
              <Button
                size="sm"
                className="mt-2 bg-[#B84B45] hover:bg-[#A33F39] text-white text-xs h-8"
                disabled={applying}
                onClick={() => doApply("replace")}
                data-testid="review-measurement-force-replace"
              >
                Vervangen
              </Button>
            </div>
          )}

          {/* v14at-p4 F5.1 (Defect J) — Hergebruik de details-INHOUD inline.
              De voormalige geneste AutoMeasureDetailsDialog opende een eigen
              Radix Dialog Portal + overlay bovenop deze review-dialog, wat
              de focus/pointer-events van de ComponentPicker-apply-knoppen
              trapte. Oplossing: één actieve modal-laag voor de reviewflow;
              de details-content hergebruiken we zonder tweede Dialog. */}
          <AutoMeasureDetailsContent building={result} />

          <ComponentPicker
            building={result}
            selected={selected}
            onToggle={toggle}
            onApply={doApply}
            onCancel={() => onOpenChange(false)}
            hasExistingSurfaces={hasExistingSurfaces}
            dataTestIdPrefix="review-measurement-picker"
            showActionsInline={false}
          />
        </div>

        {/* v14at-p7 — Sticky footer. Blijft ALTIJD bereikbaar ongeacht
            hoeveel review-content erboven staat. Bevat live-samenvatting +
            Annuleren + Meting toepassen (+ optioneel Vervang bestaande
            wanneer hasExistingSurfaces). */}
        <div
          className="shrink-0 border-t border-[#E5E2DC] bg-white px-6 py-3 flex items-center gap-3 flex-wrap"
          data-testid="review-measurement-footer"
        >
          <div className="flex-1 min-w-0">
            <ComponentPickerSummary
              building={result}
              selected={selected}
              dataTestId="review-measurement-summary"
            />
          </div>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-xs h-9"
            data-testid="review-measurement-cancel-footer"
          >
            Annuleren
          </Button>
          {hasExistingSurfaces ? (
            <>
              <Button
                variant="outline"
                onClick={() => doApply("replace")}
                disabled={selected.size === 0 || applying}
                className="border-[#B84B45] text-[#B84B45] hover:bg-[#FDF0EE] text-xs h-9"
                data-testid="review-measurement-replace-footer"
              >
                Vervang bestaande
              </Button>
              <Button
                onClick={() => doApply("keep")}
                disabled={selected.size === 0 || applying}
                className="bg-[#6B8E6B] hover:bg-[#5B7D5B] text-white text-xs h-9"
                data-testid="review-measurement-apply-footer"
              >
                {applying ? "Toepassen…" : "Meting toepassen"}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => doApply("keep")}
              disabled={selected.size === 0 || applying}
              className="bg-[#6B8E6B] hover:bg-[#5B7D5B] text-white text-xs h-9"
              data-testid="review-measurement-apply-footer"
            >
              {applying ? "Toepassen…" : "Meting toepassen"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
